<template>
  <div v-if="show" class="px-4 py-2">
    <div class="w-full max-w-md mx-auto">
      <div
        v-if="$page.props.flash.success"
        class="mb-6 flex items-center justify-between rounded-lg bg-green-500 shadow-sm"
      >
        <div class="flex items-center">
          <svg
            class="ml-4 mr-2 h-4 w-4 shrink-0 fill-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <polygon points="0 11 2 9 7 14 18 3 20 5 7 18" />
          </svg>
          <div class="py-3 text-sm md:text-base font-medium text-white">
            {{ $page.props.flash.success }}
          </div>
        </div>
        <button type="button" class="group mr-2 p-2" @click="_ => closeAlert()">
          <svg
            class="block h-2 w-2 fill-green-800 group-hover:fill-green-700"
            xmlns="http://www.w3.org/2000/svg"
            width="235.908"
            height="235.908"
            viewBox="278.046 126.846 235.908 235.908"
          >
            <path
              d="M506.784 134.017c-9.56-9.56-25.06-9.56-34.62 0L396 210.18l-76.164-76.164c-9.56-9.56-25.06-9.56-34.62 0-9.56 9.56-9.56 25.06 0 34.62L361.38 244.8l-76.164 76.165c-9.56 9.56-9.56 25.06 0 34.62 9.56 9.56 25.06 9.56 34.62 0L396 279.42l76.164 76.165c9.56 9.56 25.06 9.56 34.62 0 9.56-9.56 9.56-25.06 0-34.62L430.62 244.8l76.164-76.163c9.56-9.56 9.56-25.06 0-34.62z"
            />
          </svg>
        </button>
      </div>
      <div
        v-else-if="$page.props.flash.alert"
        class="mb-6 flex items-center justify-between rounded-lg bg-red-400 shadow-sm"
      >
        <div class="flex items-center">
          <svg
            class="ml-4 mr-2 h-4 w-4 shrink-0 fill-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm1.41-1.41A8 8 0 1 0 15.66 4.34 8 8 0 0 0 4.34 15.66zm9.9-8.49L11.41 10l2.83 2.83-1.41 1.41L10 11.41l-2.83 2.83-1.41-1.41L8.59 10 5.76 7.17l1.41-1.41L10 8.59l2.83-2.83 1.41 1.41z"
            />
          </svg>
          <div class="py-3 text-sm md:text-base font-medium text-white">
            {{ $page.props.flash.alert }}
          </div>
        </div>
        <button type="button" class="group mr-2 p-2" @click="_ => closeAlert()">
          <svg
            class="block h-2 w-2 fill-red-800 group-hover:fill-red-800"
            xmlns="http://www.w3.org/2000/svg"
            width="235.908"
            height="235.908"
            viewBox="278.046 126.846 235.908 235.908"
          >
            <path
              d="M506.784 134.017c-9.56-9.56-25.06-9.56-34.62 0L396 210.18l-76.164-76.164c-9.56-9.56-25.06-9.56-34.62 0-9.56 9.56-9.56 25.06 0 34.62L361.38 244.8l-76.164 76.165c-9.56 9.56-9.56 25.06 0 34.62 9.56 9.56 25.06 9.56 34.62 0L396 279.42l76.164 76.165c9.56 9.56 25.06 9.56 34.62 0 9.56-9.56 9.56-25.06 0-34.62L430.62 244.8l76.164-76.163c9.56-9.56 9.56-25.06 0-34.62z"
            />
          </svg>
        </button>
      </div>

      <!-- Server errors -->
      <div
        v-if="Array.isArray($page.props.errors) && $page.props.errors.length"
        class="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-medium text-red-800">
              {{ $page.props.errors.length === 1 ? 'Error' : 'Errors' }}
            </h3>
            <div class="mt-2 text-sm text-red-700">
              <ul v-if="$page.props.errors.length > 1" class="list-disc space-y-1 pl-5">
                <li v-for="error in $page.props.errors" :key="error">
                  {{ error }}
                </li>
              </ul>
              <p v-else>
                {{ $page.props.errors[0] }}
              </p>
            </div>
          </div>
          <div class="ml-auto pl-3">
            <div class="-mx-1.5 -my-1.5">
              <button
                type="button"
                @click="_ => closeAlert()"
                class="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
              >
                <span class="sr-only">Dismiss</span>
                <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'

const $page = usePage()
const show = ref(false)
const message = computed(_ => {
  return $page.props.flash.success || $page.props.flash.alert || $page.props.errors
})

show.value = !!message.value

watch([message], _ => { show.value = true })

const closeAlert = _ => { show.value = false }
</script>
